import { Flex, Text, Select, Pagination } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import HistoricalSnapshotPDFButton from "../../components/pages/HistoricalSnapshot/HistoricalSnapshotPDFButton";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import { Button } from "../../components/shared/Button/Button";
import Section from "../../components/shared/EntriesGenerator/Section/Section/Section";
import Loader from "../../components/shared/Loader/Loader";
import LogbookOverlay from "../../generator/LogbookOverlay/LogbookOverlay";
import { useGetApi } from "../../hooks";
import type {
  IHistoricalSnapshotData,
  ISnapshot,
} from "../../interfaces/HistoricalSnapshot";
import { useSelector } from "../../redux/hooks";
import { PageContainer } from "../../styled";
import { getYachtId } from "../../utils";
import {
  getWidgetHeaders,
  getWidgetRows,
} from "src/components/pages/Home/CustomDashboard/Widget.utils";
import { useIsDev } from "src/hooks/useIsDev";

interface IResData extends IHistoricalSnapshotData {
  success: boolean;
  pagecount: number;
}
interface Props {
  review?: boolean;
}

const createColumnMap = (headers: any[]) => {
  const map: Record<
    string,
    {
      path: string;
      type: string;
      name: string;
      unit: string;
      isSplit: boolean;
      isParentSplit: boolean;
    }
  > = {};

  const splitParentIndexes = new Set<number>();
  headers.forEach((header, index) => {
    if (header.type === "split" && header.cols) {
      splitParentIndexes.add(index);
    }
  });

  let flatIndex = 0;
  headers.forEach((header, headerIndex) => {
    if (header.type === "split" && header.cols) {
      map[`col_${flatIndex}`] = {
        path: `${headerIndex}`,
        type: header.type,
        name: header.name || "",
        unit: header.unit || "",
        isSplit: true,
        isParentSplit: true,
      };
      flatIndex++;

      header.cols.forEach((subHeader: any, subIndex: number) => {
        map[`col_${flatIndex}`] = {
          path: `${headerIndex},${subIndex}`,
          type: subHeader.type || header.type,
          name: subHeader.name || "",
          unit: subHeader.unit || "",
          isSplit: true,
          isParentSplit: false,
        };
        flatIndex++;
      });
    } else {
      map[`col_${flatIndex}`] = {
        path: `${headerIndex}`,
        type: header.type || "col",
        name: header.name || "",
        unit: header.unit || "",
        isSplit: false,
        isParentSplit: false,
      };
      flatIndex++;
    }
  });

  return map;
};

const HistoricalSnapshot = ({ review }: Props) => {
  const isDev = useIsDev();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logbookId, aid } = useParams();
  const { navigatedFromLogbook } = useSelector(
    state => state.generatorFeaturesSlice,
  );
  const resultsRef = useRef<HTMLDivElement>(null);
  const [previewData, setPreviewData] = useState<IHistoricalSnapshotData>();
  const [sortedList, setSortedList] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [columnMap, setColumnMap] = useState<Record<string, any>>({});

  const { data, isLoading } = useGetApi<IResData>({
    request: review ? "get-preview-reviewer" : "get-preview",
    body: {
      params: {
        lbid: logbookId,
        yachtid: getYachtId(),
        page: page,
        pageMax: pageSize,
        ...(review && { aid }),
      },
    },
    fetchOnInit: true,
  });

  useEffect(() => {
    isDev &&
      data?.list &&
      data?.list.length > 0 &&
      console.log({ list: data.list });
  }, [data]);

  useEffect(() => {
    if (data?.success) {
      const headers = ["Watchkeeper", "Events"].includes(data.lbname ?? "")
        ? getWidgetHeaders(data.headers, true)
        : data.headers;
      const list: ISnapshot[] =
        data.list.length > 0 &&
        typeof data?.list[0] === "object" &&
        "version" in data?.list[0]
          ? getWidgetRows(data.list)
          : data.list;
      const colMap = createColumnMap(headers);
      setColumnMap(colMap);

      setPreviewData({
        list: list,
        headers: headers,
        lbname: data.lbname,
      });
      setSortedList(list);
    }
  }, [data]);

  const handleHeaderClick = (columnKey: string) => {
    if (!columnMap[columnKey] || columnMap[columnKey].isParentSplit) {
      return;
    }

    const sortBy = searchParams.get("sort");
    const dir = searchParams.get("dir");

    if (sortBy === columnKey) {
      const newDir = dir === "asc" ? "desc" : "asc";
      setSearchParams({ sort: columnKey, dir: newDir }, { replace: true });
    } else {
      setSearchParams({ sort: columnKey, dir: "asc" }, { replace: true });
    }
  };

  const getValueFromPath = (row: any, columnInfo: any): any => {
    if (!columnInfo) return undefined;

    const path = columnInfo.path;
    const segments = path.split(",").map((s: string) => parseInt(s));
    const colIndex = segments[0];
    const cell = row.cols?.[colIndex];

    const value = cell?.value;

    if (
      columnInfo.unit === "sub" &&
      (value === undefined || value === "") &&
      cell?.content?.cols
    ) {
      const subCols = cell.content.cols;

      const textVal = subCols.find(
        (sc: any) => typeof sc?.value === "string" && sc.value.trim() !== "",
      )?.value;

      if (textVal) {
        return textVal;
      }

      const tankName = subCols.find(
        (sc: any) =>
          typeof sc?.value === "object" &&
          sc?.value?.data?.tankName?.trim?.() !== "",
      )?.value?.data?.tankName;

      if (tankName) {
        return tankName;
      }

      const key = subCols.find(
        (sc: any) =>
          typeof sc?.value === "object" &&
          typeof sc.value.key === "string" &&
          sc.value.key.trim() !== "",
      )?.value.key;

      if (key) {
        return key;
      }
    }

    if (segments.length === 2) {
      const [parentIdx, childIdx] = segments;
      const nested = row.cols?.[parentIdx]?.cols?.[childIdx]?.value;
      if (nested !== undefined) return nested;
    }

    const fallbackCol = row.cols.find(
      (col: any) =>
        col.name === columnInfo.name ||
        (columnInfo.unit && col.unit === columnInfo.unit),
    );
    if (fallbackCol?.value !== undefined) return fallbackCol.value;

    return value;
  };

  useEffect(() => {
    if (!previewData?.list || !columnMap) return;

    const sortBy = searchParams.get("sort");
    const dir = searchParams.get("dir") === "desc" ? -1 : 1;

    if (!sortBy || !columnMap[sortBy]) {
      setSortedList([...previewData.list]);
      return;
    }

    const columnInfo = columnMap[sortBy];

    const sorted = [...previewData.list].sort((a, b) => {
      let aValue = getValueFromPath(a, columnInfo);
      let bValue = getValueFromPath(b, columnInfo);

      const colType = columnInfo.unit;

      if (aValue && typeof aValue === "object") {
        if (colType === "date") {
          try {
            aValue = new Date(aValue).getTime();
          } catch (e) {
            aValue = 0;
          }
        } else if (colType === "time" && aValue.time) {
          aValue = aValue.time;
        } else if (
          ["user", "master", "userrefpop"].includes(colType) &&
          aValue.ref
        ) {
          aValue = parseInt(aValue.ref);
        } else {
          try {
            aValue = JSON.stringify(aValue);
          } catch (e) {
            aValue = "[object]";
          }
        }
      }

      if (bValue && typeof bValue === "object") {
        if (colType === "date") {
          try {
            bValue = new Date(bValue).getTime();
          } catch (e) {
            bValue = 0;
          }
        } else if (colType === "time" && bValue.time) {
          bValue = bValue.time;
        } else if (
          ["user", "master", "userrefpop"].includes(colType) &&
          bValue.ref
        ) {
          bValue = parseInt(bValue.ref);
        } else {
          try {
            bValue = JSON.stringify(bValue);
          } catch (e) {
            bValue = "[object]";
          }
        }
      }

      if (
        colType === "date" &&
        typeof aValue === "string" &&
        typeof bValue === "string"
      ) {
        try {
          const dateA = new Date(aValue).getTime();
          const dateB = new Date(bValue).getTime();
          return (dateA - dateB) * dir;
        } catch (e) {
          console.error("Invalid date format:", aValue, bValue);
          return 0;
        }
      }

      if (aValue === undefined || aValue === null) aValue = "";
      if (bValue === undefined || bValue === null) bValue = "";

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * dir;
      }

      return String(aValue).localeCompare(String(bValue)) * dir;
    });

    setSortedList(sorted);
  }, [searchParams, previewData?.list, columnMap]);

  const createSortIndicator = (direction: string) => {
    const indicator = document.createElement("span");
    indicator.className = "sort-indicator";
    indicator.textContent = direction === "asc" ? " ↑" : " ↓";
    Object.assign(indicator.style, {
      position: "relative",
      display: "flex",
      justifyContent: "end",
      flexWrap: "wrap",
      alignItems: "top",
      alignSelf: "top",
      marginLeft: "1px",
      lineHeight: "1.2",
      fontSize: "1em",
      verticalAlign: "middle",
      zIndex: "1000",
    });
    return indicator;
  };

  useEffect(() => {
    if (!resultsRef.current || isLoading || !columnMap) return;

    setTimeout(() => {
      const table = resultsRef.current?.querySelector("table");
      if (!table) return;

      const headerRows = table.querySelectorAll("thead tr");
      if (!headerRows.length) return;

      const sortBy = searchParams.get("sort");
      const dir = searchParams.get("dir");

      let flatIndex = 0;
      const processedCells = new Set<Element>();

      headerRows.forEach((row, rowIndex) => {
        const cells = row.querySelectorAll("th");

        cells.forEach((th, cellIndex) => {
          if (processedCells.has(th)) return;
          processedCells.add(th);

          const columnKey = `col_${flatIndex}`;
          const columnInfo = columnMap[columnKey];
          flatIndex++;

          if (!columnInfo) return;

          if (columnInfo.isParentSplit) {
            return;
          }

          if (
            th.getAttribute("colspan") &&
            parseInt(th.getAttribute("colspan") ?? "1") > 1
          ) {
            return;
          }

          th.style.cursor = "pointer";

          const newTh = th.cloneNode(true);
          th.parentNode?.replaceChild(newTh, th);

          newTh.addEventListener("click", () => {
            handleHeaderClick(columnKey);
          });

          if (columnKey === sortBy) {
            const existingIndicator = (newTh as Element).querySelector(
              ".sort-indicator",
            );
            if (existingIndicator) {
              existingIndicator.remove();
            }

            const indicator = createSortIndicator(dir ?? "asc");
            (newTh as Element).appendChild(indicator);
          }
        });
      });
    }, 200);
  }, [previewData, isLoading, searchParams, columnMap]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const section = useMemo(
    () =>
      previewData
        ? {
            headers: previewData.headers,
            rows: sortedList,
            title: "",
            colcount: 0,
            className: "passive",
          }
        : null,
    [previewData, sortedList],
  );

  if (!previewData) return null;

  if (previewData.list.length === 0)
    return (
      <div style={{ width: "100%", marginTop: 40 }}>
        <Text align="center">There is no older data for this logbook</Text>
      </div>
    );

  return (
    <PageContainer>
      <Flex gap={8} align="center" justify="space-between">
        {review ? (
          <Text size="lg">Historical Snapshot</Text>
        ) : (
          <Breadcrumbs border={false} />
        )}
        <Flex gap={12} align="center">
          {data?.pagecount && data?.pagecount > 1 ? (
            <Pagination
              mr={40}
              total={data?.pagecount}
              value={page}
              onChange={v => setPage(v)}
            />
          ) : null}
          <Text>Entries per page:</Text>
          <Select
            maw={130}
            defaultValue="25"
            allowDeselect={false}
            data={["25", "50", "100", "250"]}
            onChange={value => {
              setPageSize(parseInt(value ?? "25"));
              setPage(1);
            }}
          />
        </Flex>
      </Flex>
      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        w="100%"
        mt={20}
        mb={20}>
        {navigatedFromLogbook && (
          <Button
            variant="outline"
            onClick={handleGoBack}
            leftIcon={<IconChevronLeft />}>
            {review ? "Back to review" : "Back to entry"}
          </Button>
        )}
        {previewData.lbname && (
          <Text style={{ whiteSpace: "nowrap", fontWeight: 200 }}>
            {previewData.lbname}
          </Text>
        )}
        <Flex gap={8}>
          <HistoricalSnapshotPDFButton
            listRef={resultsRef}
            lbname={previewData.lbname}
          />
        </Flex>
      </Flex>
      {isLoading ? (
        <Loader />
      ) : (
        <div style={{ position: "relative", textAlign: "center" }}>
          {section && <LogbookOverlay sectionIndex={0} sections={[section]} />}
          {section && (
            <Section
              section={section}
              historyRows={[]}
              sIndex={0}
              horizontalScroll
              ref={resultsRef}
              snapshot
            />
          )}
        </div>
      )}
    </PageContainer>
  );
};
export default HistoricalSnapshot;
